import { useState } from "react";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { GlassCard, Loader } from "../kit";
import { Switch } from "../ui/switch";

const FREQ_LABEL = { "one-time": "One-time", monthly: "Monthly", quarterly: "Quarterly", annual: "Annual" };

function TypeRow({ t, freqs, onSaved }) {
  const [f, setF] = useState(t);
  const dirty = JSON.stringify(f) !== JSON.stringify(t);
  const save = async (next = f) => {
    try {
      await api.put(`/fees/types/${t.id}`, { name: next.name, category: next.category, frequency: next.frequency, mandatory: next.mandatory, refundable: next.refundable, enabled: next.enabled });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = (k) => (v) => { const n = { ...f, [k]: v }; setF(n); save(n); };
  const del = async () => {
    if (!window.confirm(`Remove "${t.name}"? Fees already issued under it are kept.`)) return;
    try {
      const r = await api.delete(`/fees/types/${t.id}`);
      toast.success(r.data.hidden ? "Hidden — existing fee records kept" : "Removed");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <tr className={f.enabled ? "" : "opacity-50"} data-testid={`fee-type-row-${t.id}`}>
      <td><input className="field !py-1.5 min-w-[200px]" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`fee-type-name-${t.id}`} /></td>
      <td><select className="field !py-1.5" value={f.frequency} onChange={(e) => toggle("frequency")(e.target.value)} data-testid={`fee-type-freq-${t.id}`}>
        {freqs.map((x) => <option key={x} value={x}>{FREQ_LABEL[x]}</option>)}</select></td>
      <td><button className={`rounded-full px-3 py-1 text-xs font-semibold ${f.mandatory ? "bg-blue-500/20 text-blue-200" : "bg-white/5 text-zinc-400"}`} onClick={() => toggle("mandatory")(!f.mandatory)} data-testid={`fee-type-mandatory-${t.id}`}>{f.mandatory ? "Mandatory" : "Optional"}</button></td>
      <td><Switch checked={f.refundable} onCheckedChange={toggle("refundable")} data-testid={`fee-type-refundable-${t.id}`} /></td>
      <td><Switch checked={f.enabled} onCheckedChange={toggle("enabled")} data-testid={`fee-type-enabled-${t.id}`} /></td>
      <td className="whitespace-nowrap text-right">
        {dirty && <button className="btn-primary !py-1.5 !px-3 mr-2" onClick={() => save()} data-testid={`fee-type-save-${t.id}`}><FloppyDisk size={14} /></button>}
        <button className="btn-ghost !p-2 !text-rose-300" onClick={del} data-testid={`fee-type-delete-${t.id}`}><Trash size={14} /></button>
      </td>
    </tr>
  );
}

function AddType({ categories, freqs, onSaved }) {
  const blank = { name: "", category: "Other", frequency: "one-time", mandatory: false, refundable: false, enabled: true };
  const [f, setF] = useState(blank);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fees/types", f);
      toast.success("Fee type added");
      setF(blank);
      onSaved();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <form onSubmit={submit} className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px_150px_auto]" data-testid="add-fee-type-form">
      <input className="field" required minLength={2} placeholder="New fee type, e.g. Robotics Lab Fee" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-fee-type-name" />
      <select className="field" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="new-fee-type-category">{categories.map((c) => <option key={c}>{c}</option>)}</select>
      <select className="field" value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} data-testid="new-fee-type-freq">{freqs.map((x) => <option key={x} value={x}>{FREQ_LABEL[x]}</option>)}</select>
      <button className="btn-primary" data-testid="new-fee-type-submit"><Plus size={16} weight="bold" /> Add</button>
    </form>
  );
}

export function FeeTypes() {
  const { data, loading, reload } = useApi("/fees/types");
  if (loading || !data) return <Loader />;
  return (
    <GlassCard className="p-4 sm:p-6" data-testid="fee-types-panel">
      <AddType categories={data.categories} freqs={data.frequencies} onSaved={reload} />
      {data.categories.map((cat) => {
        const rows = data.types.filter((t) => t.category === cat);
        if (!rows.length) return null;
        return (
          <div key={cat} className="mb-8">
            <div className="eyebrow mb-3">{cat} fees</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm [&_td]:px-2 [&_td]:py-2 [&_th]:px-2 [&_th]:pb-2 [&_th]:text-left [&_th]:text-[11px] [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-zinc-500">
                <thead><tr><th>Name</th><th>Frequency</th><th>Type</th><th>Refundable</th><th>Enabled</th><th></th></tr></thead>
                <tbody>{rows.map((t) => <TypeRow key={`${t.id}-${t.name}-${t.frequency}-${t.mandatory}-${t.enabled}-${t.refundable}`} t={t} freqs={data.frequencies} onSaved={reload} />)}</tbody>
              </table>
            </div>
          </div>
        );
      })}
    </GlassCard>
  );
}
